import React, { useState, useEffect, useRef, useMemo } from "react";
import toast from "react-hot-toast";
import { HiArrowNarrowLeft, HiOutlineSearch, HiX } from "react-icons/hi";

const floatingLabelInputClass = (hasError = false) => `
  block w-full px-3 py-2 bg-white border 
  ${hasError ? "border-red-500" : "border-gray-300"} 
  rounded-md shadow-sm focus:outline-none focus:ring-primary 
  focus:border-primary placeholder-transparent 
  peer
  disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100
`;

const floatingLabelClass = (hasError = false) => `
  absolute left-2 -top-2.5 px-1 text-xs transition-all 
  peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 
  peer-placeholder-shown:top-2 peer-focus:-top-2.5 peer-focus:text-xs 
  peer-focus:text-primary bg-white ${hasError ? "text-red-500" : "text-gray-600"}
  pointer-events-none
`;

const AddressAutocomplete = ({
  id,
  type = "housenumber",
  initialValue = "",
  onChange,
  onSelect,
  placeholder,
  disabled = false,
  hasError = false,
  label,
  errorMessage = "Ce champ est obligatoire",
  onNotFoundMessage = undefined,
  onNotFound = undefined,
}) => {
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hasSelected, setHasSelected] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const listRef = useRef(null);
  const selectedItemRef = useRef(null);
  const inputRef = useRef(null);

  const isMobile = useMemo(() => window.innerWidth < 768, []);

  useEffect(() => {
    if (initialValue?.length > 0) setHasSelected(true);
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (hasSelected) return;

    if (value.length >= 2) setIsLoading(true);

    const delayDebounceFn = setTimeout(() => fetchSuggestions(value), 500);

    return () => clearTimeout(delayDebounceFn);
  }, [value, type, hasSelected]);

  const fetchSuggestions = async (value) => {
    if (value.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const encodedValue = encodeURIComponent(value.trim());
      const res = await fetch(`https://data.geopf.fr/geocodage/search/?q=${encodedValue}&limit=15&type=${type}`);
      const data = await res.json();

      if (!data || !data.features) {
        setSuggestions([]);
        setShowSuggestions(true);
        return;
      }

      setSuggestions(data.features);
      setShowSuggestions(true);
      setSelectedIndex(0);
    } catch (error) {
      toast.error("Une erreur est survenue lors de la recherche.");
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (suggestion) => {
    onSelect(suggestion);
    setHasSelected(true);
    setShowSuggestions(false);
    setSuggestions([]);
    setIsFocused(false);
  };

  const handleClose = () => {
    setShowSuggestions(false);
    setSuggestions([]);
    if (inputRef.current) inputRef.current.blur();
    setIsFocused(false);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    const totalItems = onNotFound ? suggestions.length + 1 : suggestions.length;
    if (totalItems === 0) return;

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (onNotFound && selectedIndex === suggestions.length) {
        onNotFound();
        setHasSelected(true);
        setShowSuggestions(false);
        setSuggestions([]);
      } else if (suggestions[selectedIndex]) {
        handleSelect(suggestions[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleClose();
    }
  };

  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [selectedIndex]);

  return (
    <>
      <div className="mb-1">
        <div className="relative">
          <div className="relative">
            <input
              ref={inputRef}
              id={id}
              type="text"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                onChange(e.target.value);
                setHasSelected(false);
              }}
              onBlur={() => {
                if (!isMobile) setTimeout(() => setShowSuggestions(false), 200);
              }}
              onFocus={() => {
                setIsFocused(true);
                if (!hasSelected) fetchSuggestions(value);
              }}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              className={floatingLabelInputClass(hasError)}
            />
            <label htmlFor={placeholder} className={floatingLabelClass(hasError)}>
              {label}
            </label>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              {isLoading ? <div className="h-4 w-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" /> : <HiOutlineSearch className="h-5 w-5 text-gray-400" />}
            </div>
          </div>

          {showSuggestions && !isMobile && (
            <ul ref={listRef} className="absolute z-10 w-full bg-white shadow-lg max-h-60 rounded-md text-base overflow-auto focus:outline-none sm:text-sm mt-1">
              {suggestions.map((suggestion, index) => (
                <li
                  key={index}
                  ref={index === selectedIndex ? selectedItemRef : null}
                  className={`text-gray-900 cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100 ${index === selectedIndex ? "bg-gray-100" : ""}`}
                  onMouseDown={() => handleSelect(suggestion)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  {suggestion.properties.label}
                </li>
              ))}
              {onNotFound && (
                <li
                  ref={selectedIndex === suggestions.length ? selectedItemRef : null}
                  className={`text-primary cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100 hover:text-primary-600 ${
                    selectedIndex === suggestions.length ? "bg-gray-100" : ""
                  }`}
                  onMouseDown={() => {
                    onNotFound();
                    setHasSelected(true);
                    setShowSuggestions(false);
                    setSuggestions([]);
                  }}
                  onMouseEnter={() => setSelectedIndex(suggestions.length)}
                >
                  {onNotFoundMessage || "Aucun résultat trouvé. Cliquer ici pour saisir manuellement"}
                </li>
              )}
              {!onNotFound && suggestions.length === 0 && <li className={`text-gray-900 cursor-default select-none relative py-2 pl-3 pr-9`}>Aucun résultat trouvé.</li>}
            </ul>
          )}
        </div>

        <p className={`text-red-500 text-xs ${hasError ? "visible" : "invisible"}`}>{errorMessage}</p>
      </div>

      {isMobile && isFocused && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col md:hidden">
          <div className="flex items-center p-2 border-b border-gray-200">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Fermer"
            >
              <HiArrowNarrowLeft className="h-6 w-6 text-gray-600" />
            </button>
            <div className="font-semibold">{label}</div>
          </div>
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="relative">
              <input
                type="text"
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  onChange(e.target.value);
                  setHasSelected(false);
                }}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                autoFocus
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                {isLoading ? (
                  <div className="h-4 w-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                ) : (
                  <HiOutlineSearch className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>
          </div>
          <ul ref={listRef} className="flex-1 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <li
                key={index}
                ref={index === selectedIndex ? selectedItemRef : null}
                className={`text-gray-900 cursor-pointer select-none relative py-4 px-4 border-b border-gray-100 ${index === selectedIndex ? "bg-gray-50" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(suggestion);
                }}
              >
                {suggestion.properties.label}
              </li>
            ))}
            {onNotFound && (
              <li
                ref={selectedIndex === suggestions.length ? selectedItemRef : null}
                className={`text-primary cursor-pointer select-none relative py-4 px-4 border-b border-gray-100 ${selectedIndex === suggestions.length ? "bg-gray-50" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onNotFound();
                  setHasSelected(true);
                  setShowSuggestions(false);
                  setSuggestions([]);
                  setIsFocused(false);
                }}
              >
                {onNotFoundMessage || "Aucun résultat trouvé. Cliquer ici pour saisir manuellement"}
              </li>
            )}
            {!onNotFound && suggestions.length === 0 && <li className="text-gray-500 cursor-default select-none relative py-4 px-4 text-center">Aucun résultat trouvé.</li>}
          </ul>
        </div>
      )}
    </>
  );
};

export default AddressAutocomplete;
